import React, { useState } from 'react';
import { useController, UseControllerProps } from 'react-hook-form';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Autocomplete, Box, Button, Checkbox, CircularProgress, createFilterOptions, FormControl, IconButton, ListItemText, MenuItem, TextField, Tooltip } from '@mui/material';
import { formControlColour, tooltipColour } from '../BEE123.Constants';
import { AttendeeLookup } from '../../../Model/Dto/WSP/Attendees/AttendeeLookup';


interface IProps extends UseControllerProps {
    label?: string;
    options: AttendeeLookup[];
    size?: "small" | "medium";
    loading?: boolean;
    limit?: number;
    onChange?: (value: any) => void;
    disableEntries? : boolean;
    onAdd: () => void;
    isAncillaryCost?: boolean
}

const AppAutoComplete = (props: IProps) => {
    const [isSelectAll, setSelectAll] = useState<boolean>(false);
    const filterOptions: any = createFilterOptions<any>({
        matchFrom: 'any',
        stringify: (option) => (option.name + ' ' + option.idNumber + ' ' + option.jobTitleAndEmployeeCode),
        limit: props.limit === undefined ? 20 : props.limit,
    });

    const handleAdd = () => {
        props.onAdd(); 
        field.onChange([]);
    }

    const handleSelectAllChange = () => {
        setSelectAll(prev => !prev);

        let selectedValues: any[];

        if(props.disableEntries)
        {
            selectedValues = props.options
                .filter((item: AttendeeLookup) => item.isEmploymentWithinIntervention)
                .map((item: AttendeeLookup) => item.platformPeopleId);
        }
        else
        {
            selectedValues = props.options.map((item: AttendeeLookup) => item.platformPeopleId)
        }
    

        field.onChange(selectedValues);

        if (props.onChange) {
            props.onChange(selectedValues);
        }
    };

    const selectAllOption = {
        platformPeopleId: 'selectAll',
        name: 'Select All',
        idNumber: '',
        jobTitleAndEmployeeCode: '',
    };
    const allOptions = [selectAllOption, ...props.options];

    const { fieldState, field } = useController({ ...props, defaultValue: [] });
    const selectedOptions = props.options.filter(option => (field.value || []).includes(option.platformPeopleId));
    return (
        <>
        <FormControl fullWidth error={!!fieldState.error}>
            <Autocomplete
                {...props}
                {...field}
                limitTags={2}
                onChange={(event, value: any) => {
                    const hasSelectAll = value.some(
                        (item: AttendeeLookup) => item.platformPeopleId === selectAllOption.platformPeopleId
                    );

                    if (hasSelectAll) {
                        handleSelectAllChange();
                    } else {
                        let selectedValues : any[]

                        if(props.disableEntries)
                        {
                            selectedValues = value
                            .filter((item: AttendeeLookup) => item.isEmploymentWithinIntervention)
                            .map((item: AttendeeLookup) => item.platformPeopleId);
                        }
                        else
                        {
                            selectedValues = value.map((item: AttendeeLookup) => item.platformPeopleId)
                        }
                        
                        field.onChange(selectedValues);

                        if (selectedValues.length === props.options.length) {
                            setSelectAll(true);
                        }
                        else {
                            setSelectAll(false);
                        }
                        if (props.onChange) {
                            props.onChange(selectedValues);
                        }
                    }
                }}
                value={selectedOptions}
                multiple
                disableCloseOnSelect
                options={allOptions}
                filterOptions={filterOptions}
                getOptionLabel={option => option.name}
                isOptionEqualToValue={(option, value) => option.lookupId === value.lookupId}
                loading={props.loading}
                renderOption={(optionProps, option) => (
                    <MenuItem key={option.platformPeopleId} value={option.platformPeopleId} {...optionProps}>
                        {option.platformPeopleId === 'selectAll' &&
                            <Checkbox
                                checked={isSelectAll}
                                onChange={() => { console.log('SetSelected', isSelectAll); }}
                            />
                        }
                        {option.platformPeopleId !== 'selectAll' &&
                            <Checkbox
                                style={props.disableEntries ? { visibility: option.isEmploymentWithinIntervention ? 'visible' : 'hidden' } : {}}
                                checked={selectedOptions.some((item: AttendeeLookup) => item.platformPeopleId === option.platformPeopleId)}
                            />
                          
                        }
                        <ListItemText
                            primaryTypographyProps={{ fontSize: "0.875rem" }}
                            secondaryTypographyProps={{ fontSize: "0.875rem" }}
                            primary={option.name}
                            secondary={
                                <>
                                    {option.platformPeopleId !== 'selectAll'
                                        &&
                                        <>
                                            <span>
                                                {option.idNumber}
                                            </span>
                                            <br />
                                            <span>{option.jobTitleAndEmployeeCode}</span>
                                        </>}
                                </>
                            }
                        />
                            {(props.disableEntries && !option.isEmploymentWithinIntervention && option.platformPeopleId !== 'selectAll') && (
                                <Tooltip
                                    title={'This person does not have an employment data record at the time of the intervention.'}
                                    arrow
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: tooltipColour,
                                                '& .MuiTooltip-arrow': {
                                                    color: tooltipColour
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        edge="end"
                                        color="inherit"
                                        aria-label="info"
                                    >
                                        <InfoOutlinedIcon style={{ color: formControlColour }} />
                                    </IconButton >
                                </Tooltip>
                            )}

                    </MenuItem>
                )}
                renderInput={
                    (params) => <TextField
                        {...params}
                        fullWidth
                        label={props.label}

                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {props.loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                        variant="outlined"
                        size={props.size}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                    />
                }
            />
        </FormControl>
            <Box pt={1}>
            <Button color="primary" variant="contained" onClick={() => { handleAdd() }}>
                {props.isAncillaryCost ? "ASSOCIATE ATTENDEES TO COST" : "ADD TO ATTENDEES"}
            </Button>
        </Box>
        </>
    )
}

export default AppAutoComplete